import { useEffect, useMemo, useState } from "react"
import useSWRMutation from "swr/mutation"
import { formatToISO, setToFirstDayOfMonth, setToLastDayOfMonth } from "@/components/Calendar/utils"
import { EVENT_QUERY } from "@/pages/api/fragments"
import dayjs from "dayjs"
import { fetcher, mergeArrays } from "@/utils/utils"
import { LOCALES } from "@/constants"

const query = `
		query GetEventsData($start: DateTime, $end: DateTime, $locale: SiteLocale)  {
			calendar {
				events: _allReferencingEvents(locale: $locale, filter: { start: {gte: $start, lt: $end } }) {
					${EVENT_QUERY}
				}
			}
		}
`

const initAllEvents = Object.values(LOCALES).reduce(
	(prev, acc) => ({
		...prev,
		[acc]: [],
	}),
	{},
)

export default function useEvents(start, end, locale = LOCALES.PL) {
	const [shouldFetch, setShouldFetch] = useState(false) // State to control fetching
	const [allEvents, setAllEvents] = useState(initAllEvents) //container for all events loaded

	const formatted = useMemo(() => {
		const dateStart = new Date(start)
		const dateEnd = new Date(end)
		const firstDayOfMonth = setToFirstDayOfMonth(dateStart)
		const lastDayOfMonth = setToLastDayOfMonth(dateEnd)
		const dayJSStart = dayjs(firstDayOfMonth)
		const dayJSEnd = dayjs(lastDayOfMonth)
		const shouldFetch = dayJSStart.isBefore(dayJSEnd) //comparing whether there is logical sense between date's
		setShouldFetch(shouldFetch) //so data can be updated
		const formattedStart = formatToISO(firstDayOfMonth)
		const formattedEnd = formatToISO(lastDayOfMonth)
		return {
			start: formattedStart,
			end: formattedEnd,
		}
	}, [start, end])

	const requestBody = useMemo(() => {
		return {
			query,
			variables: {
				start: formatted.start,
				end: formatted.end,
				locale: locale,
			},
		}
	}, [formatted.start, formatted.end, locale])

	const { data, isLoading, error, trigger } = useSWRMutation(
		["/api/getData", requestBody],
		fetcher,
		{ revalidateOnFocus: false, revalidateOnMount: false, populateCache: true }, //should prevent unwanted calls
	)

	useEffect(() => {
		if (shouldFetch) {
			trigger() //trigger new api call
		}
	}, [trigger, shouldFetch, formatted.start, formatted.end, locale])

	useEffect(() => {
		const newEvents = data?.data?.calendar?.events || []
		if (newEvents.length > 0) {
			// Merge the newly fetched data with previously stored data
			setAllEvents((prevEvents) => {
				const prev = prevEvents[locale] || []
				const updated = mergeArrays(prev, newEvents, (a, b) => a.id === b.id)
				return { ...prevEvents, [locale]: updated }
			})
		}
	}, [data])

	// useEffect(() => {
	// 	console.log(allEvents);
	// },[allEvents])

	return {
		events: allEvents[locale],
		isLoading,
		error,
	}
}
